import type { EventRead } from '@models/api';
import { buildEventContextPayload, type EventContextPayload } from './eventContext';
import { buildEventParentSpacePayload, type SingleSpacePayload } from './spaceContext';

/**
 * The standard event + parent-space context every helpable-at-live-event
 * analytics event carries (Track 2 Phase A, spec 3.5). It is exactly
 * `buildEventContextPayload` merged with `buildEventParentSpacePayload` - the
 * same id/name/slug fields the rest of the live layer stamps - so helpable
 * events segment on the same keys as `live_surface_displayed` everywhere else.
 */
export type HelpableEventContext = EventContextPayload & SingleSpacePayload;

/**
 * Build the helpable event context from the live event.
 *
 * `buildEventParentSpacePayload` is used (not `buildSingleSpacePayload(event.space)`)
 * so a cache-cold event carrying only `space_id` still emits the id - the house
 * rule for event-scoped analytics.
 *
 * `fallbackEventId` backstops the one caller (ForYouSection) that owns the event
 * only as an identifier string: when no event object is threaded, the context
 * still emits `event_id` (name/slug/space stay null) rather than dropping the
 * event entirely. Callers with the full event (pill chip, feed chips) omit it.
 */
export const buildHelpableEventContext = (
  event: EventRead | null | undefined,
  fallbackEventId?: string,
): HelpableEventContext => {
  const context: HelpableEventContext = {
    ...buildEventContextPayload(event),
    ...buildEventParentSpacePayload(event),
  };
  if (!context.event_id && fallbackEventId) {
    return { ...context, event_id: fallbackEventId };
  }
  return context;
};
